// Lightweight global error reporter.
//
// We don't run Sentry yet — no DSN, no separate account, no SDK in
// node_modules. But going live with zero visibility into client-side
// errors would be flying blind. This installs window-level handlers
// for uncaught errors + unhandled rejections and surfaces them via
//
//   1. console.error (always — keeps DevTools surface useful)
//   2. window.plausible('Error', { ... }) (when Plausible is loaded)
//
// Plausible event-property values are capped at 2 000 chars and only
// strings/numbers/booleans survive. We strip stack traces to the top
// frame, sanitize file paths, and keep messages short.
//
// To add Sentry later: install @sentry/react, call its init() from a
// new module, and the two systems can coexist — this reporter does
// not swallow errors, just observes them.

import { trackEvent } from './analytics';
import { isReloadPending } from './chunkReload';

const MAX_MESSAGE_LEN = 240;

// De-dupe: if the same error fires hundreds of times in a render
// loop, we'd burn through the Plausible event budget for nothing.
// 60 s sliding window keyed on (message + first stack frame).
const SEEN = new Map();
const DEDUPE_WINDOW_MS = 60_000;

function isDuplicate(key) {
  const now = Date.now();
  // GC expired entries first so SEEN doesn't grow forever.
  for (const [k, ts] of SEEN) {
    if (now - ts > DEDUPE_WINDOW_MS) SEEN.delete(k);
  }
  if (SEEN.has(key)) return true;
  SEEN.set(key, now);
  return false;
}

function shortenStackTopFrame(stack) {
  if (typeof stack !== 'string') return undefined;
  const firstLine = stack.split('\n').find(l => l.trim().startsWith('at '));
  if (!firstLine) return undefined;
  // Strip the absolute origin so the event prop is the same regardless
  // of dev/staging/prod build.
  return firstLine.replace(/https?:\/\/[^/]+/, '').trim().slice(0, 160);
}

function report(kind, message, stack) {
  // Folgefehler beim Neuladen nach einem Deploy nicht zählen.
  if (isReloadPending()) return;
  const msg = String(message ?? '').slice(0, MAX_MESSAGE_LEN);
  const frame = shortenStackTopFrame(stack);
  const key = `${kind}::${msg}::${frame ?? ''}`;
  if (isDuplicate(key)) return;

  // Console gets the full thing; Plausible gets the dedupable summary.
  console.error(`[${kind}]`, message, stack || '');

  try {
    trackEvent('Error', {
      kind,
      message: msg,
      ...(frame ? { frame } : {})
    });
  } catch {
    // analytics.trackEvent already swallows, but belt-and-suspenders.
  }
}

/**
 * Install the global handlers. Safe to call more than once — second
 * call is a no-op via a module-scoped flag.
 *
 * Call this from a top-level module before any heavy code runs so a
 * synchronous error in the React mount is also captured. main.jsx is
 * the right place.
 */
let installed = false;
let onErrorHandler = null;
let onRejectionHandler = null;

export function installErrorReporter() {
  if (installed) return;
  if (typeof window === 'undefined') return;
  installed = true;

  onErrorHandler = (event) => {
    // event.error is the Error instance for code errors; for resource
    // loading failures (img src, script src) the event is dispatched
    // on the element and has no .error — skip those, they're noisy.
    if (!event.error) return;
    report('uncaught', event.error.message || event.message, event.error.stack);
  };

  onRejectionHandler = (event) => {
    const reason = event.reason;
    const message = reason instanceof Error
      ? reason.message
      : (typeof reason === 'string' ? reason : 'Unhandled promise rejection');
    const stack = reason instanceof Error ? reason.stack : undefined;
    report('rejection', message, stack);
  };

  window.addEventListener('error', onErrorHandler);
  window.addEventListener('unhandledrejection', onRejectionHandler);
}

/**
 * Test-only: tear down listeners + clear de-dupe state so each test
 * starts from zero. Not used in production code paths.
 */
export function __resetErrorReporterForTests() {
  if (typeof window !== 'undefined') {
    if (onErrorHandler)     window.removeEventListener('error', onErrorHandler);
    if (onRejectionHandler) window.removeEventListener('unhandledrejection', onRejectionHandler);
  }
  onErrorHandler = null;
  onRejectionHandler = null;
  installed = false;
  SEEN.clear();
}
